import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert
} from '@mui/material';
import api from '../lib/api';
import { useQuery } from '@tanstack/react-query';

const FidelityDashboard: React.FC = () => {
  const { id: agreementId } = useParams<{ id: string }>();

  const { data: fidelityData, isLoading } = useQuery({
    queryKey: ['fidelity', agreementId],
    queryFn: async () => {
      const res = await api.get(`/metrics/fidelity/${agreementId}`);
      return res.data.data;
    },
    enabled: !!agreementId,
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'High Fidelity';
    if (score >= 60) return 'Moderate Fidelity';
    if (score >= 40) return 'Low Fidelity';
    return 'Poor Fidelity';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Typography>Loading fidelity data...</Typography>
      </Box>
    );
  }

  if (!fidelityData) {
    return (
      <Alert severity="info">
        No fidelity data available yet. Start completing tasks to track fidelity.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Overall Fidelity Score */}
      <Paper sx={{ p: 4, mb: 3, textAlign: 'center' }}>
        <Typography variant="h2" color="primary" gutterBottom>
          {fidelityData.overallFidelity}%
        </Typography>
        <Chip
          label={getScoreLabel(fidelityData.overallFidelity)}
          color={getScoreColor(fidelityData.overallFidelity)}
          size="medium"
          sx={{ mb: 2 }}
        />
        <LinearProgress
          variant="determinate"
          value={fidelityData.overallFidelity}
          color={getScoreColor(fidelityData.overallFidelity)}
          sx={{ height: 12, borderRadius: 3 }}
        />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Plan adherence score
        </Typography>
      </Paper>

      {/* Fidelity Breakdown */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {fidelityData.taskFidelity}%
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Task Completion
              </Typography>
              <LinearProgress
                variant="determinate"
                value={fidelityData.taskFidelity}
                color={getScoreColor(fidelityData.taskFidelity)}
                sx={{ height: 6, borderRadius: 3, mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main" gutterBottom>
                {fidelityData.onTimeCompletion}%
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                On Time
              </Typography>
              <LinearProgress
                variant="determinate"
                value={fidelityData.onTimeCompletion}
                color={getScoreColor(fidelityData.onTimeCompletion)}
                sx={{ height: 6, borderRadius: 3, mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main" gutterBottom>
                {fidelityData.milestoneFidelity}%
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Milestones
              </Typography>
              <LinearProgress
                variant="determinate"
                value={fidelityData.milestoneFidelity}
                color={getScoreColor(fidelityData.milestoneFidelity)}
                sx={{ height: 6, borderRadius: 3, mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fidelity Alerts */}
      {fidelityData.overallFidelity < 60 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Consider reviewing your plan and addressing gaps
        </Alert>
      )}

      {fidelityData.details.lateTasks > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fidelityData.details.lateTasks} task{fidelityData.details.lateTasks > 1 ? 's' : ''} overdue
        </Alert>
      )}

      {fidelityData.overallFidelity >= 80 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Excellent adherence to your implementation plan
        </Alert>
      )}

      {/* Historical Trend */}
      {fidelityData.historical && fidelityData.historical.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Recent History
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
            {fidelityData.historical.map((metric: any, idx: number) => (
              <Chip
                key={idx}
                label={`${new Date(metric.metricDate).toLocaleDateString()}: ${metric.value}%`}
                color={getScoreColor(metric.value)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default FidelityDashboard;

